import { useState, useCallback, useEffect } from "react";
import { useAuth } from "contexts";
import { ProfileHandling } from "services";
import { UserData, UpdateUserInput } from "types";
import { fetchAuthSession, fetchUserAttributes } from "aws-amplify/auth";

const useUserData = () => {
  const { authState } = useAuth();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback((error: unknown, message: string) => {
    const errorDetails = error instanceof Error ? error.message : String(error);
    const fullMessage = `${message}: ${errorDetails}`;
    console.error(fullMessage, error);
    setError(fullMessage);
    return error instanceof Error ? error : new Error(fullMessage);
  }, []);

  const fetchUserData = useCallback(
    async (userId?: string) => {
      if (!authState?.isSignedIn) {
        setError("Authentication not ready");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const sub = (await fetchUserAttributes()).sub;
        const idToFetch = userId || `${sub}::${sub}`;

        if (!idToFetch) throw new Error("UserID not found");

        const idToken = (await fetchAuthSession()).tokens?.idToken;
        const userData = await ProfileHandling.getUserDataFromToken(idToken);

        const response: any = await ProfileHandling.fetchUser(idToFetch);

        if (response.data) {
          response.data.idToken = userData.idToken;
          setUserData(response.data);
        }
      } catch (error) {
        handleError(error, "Failed to load profile data");
      } finally {
        setIsLoading(false);
      }
    },
    [authState?.isSignedIn, handleError]
  );

  const updateUserData = async (input: UpdateUserInput): Promise<boolean> => {
    setError(null);
    try {
      if ("profilePictureUrl" in input) {
        input.profilePictureUrl = input.profilePictureUrl || "";
      }

      const response: any = await ProfileHandling.updateUser(input);

      // Check for GraphQL errors first
      if (response && response.errors && response.errors.length > 0) {
        const errorMessages = response.errors
          .map((err: any) => err.message)
          .join(", ");
        throw new Error(`GraphQL errors: ${errorMessages}`);
      }

      if (response && response.data) {
        setUserData((prev) => ({ ...prev, ...response.data }));
        return true;
      }

      if (response) {
        if (
          response.success ||
          response.status === 200 ||
          response.statusCode === 200
        ) {
          return true;
        }
      }

      throw new Error("Failed to update user data");
    } catch (error) {
      handleError(error, "Failed to update user data");
      return false;
    }
  };

  useEffect(() => {
    if (authState?.isSignedIn) {
      fetchUserData();
    } else {
      setIsLoading(false);
      setUserData(null);
    }
  }, [authState?.isSignedIn, fetchUserData]);

  return {
    userData,
    isLoading,
    error,
    fetchUserData,
    updateUserData,
  };
};

export default useUserData;
